import { auth, db } from "@/service/firebase";
import { doc, runTransaction, serverTimestamp, setDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { createUsageOutbox, usageFingerprint, validUsageManifest } from "./molduraUsageOutbox";

const prepared = new Map();
let currentTotem = () => null;
let draining = null;
function context() {
  const uid = auth?.currentUser?.uid, project = db?.app?.options?.projectId;
  const key = uid && project ? `${project}:${uid}` : null;
  let outbox = null;
  try { outbox = createUsageOutbox(typeof window !== "undefined" ? window.localStorage : null, project, uid); }
  catch (_) { /* Immediate delivery remains possible; no claim of durability. */ }
  return { key, outbox };
}
const sameContext = expected => expected.key !== null && context().key === expected.key;
function warn() { console.warn("Não foi possível confirmar o uso de molduras. Aceites guardados localmente serão reenviados; confira conexão e espaço local."); }

// The local snapshot is frozen after export. A failed preparation upload does
// not change which composition the service subsequently accepted.
export function prepararUsoMolduras(manifest) {
  const owner = context();
  try { owner.outbox?.clearPrepared(); } catch (_) { warn(); }
  if (!manifest) { prepared.clear(); return Promise.resolve(false); }
  prepared.clear();
  if (!validUsageManifest(manifest)) return Promise.resolve(false);
  const snapshot = JSON.parse(JSON.stringify(manifest));
  prepared.set(manifest.compraId, { manifest: snapshot, key: owner.key });
  try { owner.outbox?.prepare(snapshot); } catch (_) { warn(); }
  return setDoc(doc(db, "compras", manifest.compraId), { uso_molduras_preparado_v1: snapshot }, { merge: true }).then(() => true);
}
async function deliver(record, owner) {
  const allowed = () => sameContext(owner) && (!currentTotem() || currentTotem() === record.manifest.totemId);
  if (!allowed()) return "PENDING";
  return runTransaction(db, async transaction => {
    if (!allowed()) return "PENDING";
    const manifest = record.manifest, reference = doc(db, "compras", manifest.compraId);
    const snapshot = await transaction.get(reference);
    if (!allowed()) return "PENDING";
    if (!snapshot.exists()) return "PENDING";
    const data = snapshot.data();
    if (data.totem_id !== manifest.totemId) return "REVIEW";
    if (data.uso_molduras_aceite_v1) {
      const prior = { ...data.uso_molduras_aceite_v1 };
      ["natureza", "recebidoEm", "observadoEm", "origemHorarioObservado", "versaoPrograma"].forEach(key => { delete prior[key]; });
      return usageFingerprint(prior) === usageFingerprint(manifest) ? "DUPLICATE" : "REVIEW";
    }
    if (data.pago !== true && data.pagamento_desativado_na_compra !== true) return "PENDING";
    transaction.update(reference, { uso_molduras_aceite_v1: { ...manifest, natureza: "ACEITE_SERVICO",
      recebidoEm: serverTimestamp(), observadoEm: record.aceitoEm, origemHorarioObservado: "CLIENTE_DIAGNOSTICO",
      versaoPrograma: String(process.env.VUE_APP_VERSION || "nao-informada").slice(0, 64) } });
    return "SENT";
  });
}
async function attempt(record, owner) {
  try {
    const result = await deliver(record, owner);
    if (!sameContext(owner)) return false;
    if (["SENT", "DUPLICATE"].includes(result)) owner.outbox?.acknowledge(record);
    else if (result === "REVIEW") { owner.outbox?.review(record); console.warn("Uso de molduras exige conferência; o primeiro aceite foi preservado."); }
    return result === "SENT";
  } catch (_) { warn(); return false; }
}
export async function registrarAceiteUsoMolduras(compraId, totemId, saidasAceitas) {
  if (!/^[A-Za-z0-9_-]{1,128}$/.test(compraId || "") || !/^[A-Za-z0-9_-]{1,128}$/.test(totemId || "")
    || !Number.isSafeInteger(saidasAceitas) || saidasAceitas < 1) return false;
  const owner = context(), saved = prepared.get(compraId);
  const manifest = saved?.key === owner.key ? saved.manifest : owner.outbox?.prepared(totemId, compraId);
  if (!sameContext(owner) || !validUsageManifest(manifest) || manifest.totemId !== totemId || manifest.saidas.length !== saidasAceitas) return false;
  let record = { versao: 1, manifest, saidasAceitas, aceitoEm: new Date().toISOString() };
  try { record = owner.outbox?.enqueue(record) || record; }
  catch (error) {
    warn();
    if (error.code === "USAGE_CONFLICT") return false;
    // Full/unavailable storage must not lose an immediately deliverable event.
    return attempt(record, { key: owner.key, outbox: null });
  }
  return attempt(record, owner);
}
export function reenviarAceitesUsoMolduras(totemId = currentTotem()) {
  if (draining) return draining;
  const owner = context();
  if (!sameContext(owner) || !totemId) return Promise.resolve(false);
  let records;
  try { records = owner.outbox?.pending(totemId).slice(0, 10) || []; } catch (_) { warn(); return Promise.resolve(false); }
  draining = (async () => {
    for (const record of records) {
      if (!sameContext(owner) || currentTotem() && currentTotem() !== totemId) break;
      await attempt(record, owner);
    }
    return true;
  })().finally(() => { draining = null; });
  return draining;
}
export function iniciarEnvioUsoMolduras(getTotemId) {
  currentTotem = typeof getTotemId === "function" ? getTotemId : () => null;
  // Optional telemetry must not prevent the kiosk from booting if Auth is not
  // initialized (or is unavailable in an embedded/test environment).
  if (!auth || typeof window === "undefined") return () => {};
  const run = () => { reenviarAceitesUsoMolduras().catch(warn); };
  let cancelAuth = () => {};
  try { cancelAuth = onAuthStateChanged(auth, run); } catch (_) { warn(); }
  window.addEventListener("online", run);
  const timer = window.setInterval(run, 30000);
  run();
  return () => { cancelAuth(); window.removeEventListener("online", run); window.clearInterval(timer); };
}
